import backgroundVideoUrl from 'assets/videos/video-transition-wave-blur-bigger.mp4';
import { type FunctionComponent, useState } from 'react';

import { useNavigate } from 'react-router';
import { time } from '@dr.pogodin/react-utils';

import PageWithVideoBackground from 'components/PageWithVideoBackground';

import { ChatInterface } from 'components/ai-app';
import { useExperience, useSession, useWellbeingAgent } from 'hooks';

import { getRealtimeAiClient } from 'services';

import { processInterview } from 'services/ai';
import {
  type InsightT,
  QuestionnaireKinds,
  addInsights,
  useGlobalState,
} from 'utils';

import backgroundImageUrl from 'assets/images/bg-transitions-blur.png';

import './style.scss';

export const WellnessCheckOut: FunctionComponent = () => {
  const [audioSetup] = useGlobalState('ui.audioSetup');

  const [userId] = useGlobalState('user.id');
  if (!userId) throw Error('Not authenticated');

  const experience = useExperience();
  const navigate = useNavigate();
  const session = useSession();

  const [
    insights,
    setInsights,
  ] = useState<Record<string, InsightT[]>>({});

  // eslint-disable-next-line react/hook-use-state
  const [startedAt] = useState(Date.now);

  // eslint-disable-next-line react/hook-use-state
  const [, setProgress] = useState(0);

  const { noAudioInputAfterMessageId, state, transcript } = useWellbeingAgent({
    experience: experience.data?.experience,
    mode: 'WELLBEING_CHECK_OUT',
    onSignal: (message) => {
      switch (message.kind) {
        case 'END':
          void (async () => {
            const realtime = await getRealtimeAiClient();
            const transcript2 = realtime.getCurrentTranscript();

            const sessionId = session.data?.id;
            if (!sessionId) throw Error('Internal error');

            void processInterview({
              kind: QuestionnaireKinds.POST_SESSION__AI_REALTIME,
              sessionId: session.data?.id,
              startedAt,
              transcript: transcript2,
              userDateTime: time(startedAt).toString(),
              userId,
            });

            await navigate('/');
          })();
          break;
        case 'INSIGHT':
          void (async () => {
            const realtime = await getRealtimeAiClient();
            const transcript2 = realtime.getCurrentTranscript();
            setInsights(
              (old) => addInsights(old, transcript2, message.insights),
            );
          })();
          break;
        case 'PROGRESS_UPDATE':
          setProgress(message.progress);
          break;

        // NOTE: "INTERVIEW_BRANCH_CHANGE" is not supposed to be emitted in
        // the wellness check-out flow.
        case 'CHECK_IN_DONE':
        case 'DIRECTIVE_SESSION_REQUEST':
        case 'INTERVIEW_SEGMENT_CHANGE':
        default: throw Error('Unexpected case');
      }
    },
    sessionKind: experience.data?.sessionKind,
  });

  return (
    <PageWithVideoBackground
      fallbackImageUrl={backgroundImageUrl}
      videoUrl={backgroundVideoUrl}
    >
      {
        audioSetup ? <div /> : (
          <div styleName="container">
            <ChatInterface
              agentState={state}
              hideInsights
              initialInputMode="mic"
              insights={insights}
              noAudioInputAfterMessageId={noAudioInputAfterMessageId}
              transcript={transcript}
            />
          </div>
        )
      }
    </PageWithVideoBackground>
  );
};
